import React from 'react';
import {Alert, FormGroup} from 'reactstrap';

const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
const scriptId = 'google-recaptcha-v3-script';
let recaptchaScriptPromise;

const loadRecaptchaScript = () => {
  if (!siteKey) return Promise.reject(new Error('missing-site-key'));
  if (window.grecaptcha) return Promise.resolve(window.grecaptcha);
  if (recaptchaScriptPromise) return recaptchaScriptPromise;

  recaptchaScriptPromise = new Promise((resolve, reject) => {
    const existingScript = document.getElementById(scriptId);

    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(window.grecaptcha));
      existingScript.addEventListener('error', reject);
      return;
    }

    const script = document.createElement('script');
    script.id = scriptId;
    script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.grecaptcha);
    script.onerror = reject;
    document.body.appendChild(script);
  });

  return recaptchaScriptPromise;
};

export const useCaptcha = () => {
  const [error, setError] = React.useState('');

  const execute = React.useCallback(async action => {
    if (!siteKey) {
      setError('reCAPTCHA is not configured. Please set REACT_APP_RECAPTCHA_SITE_KEY.');
      return null;
    }

    try {
      const grecaptcha = await loadRecaptchaScript();
      const token = await new Promise((resolve, reject) => {
        grecaptcha.ready(() => {
          grecaptcha.execute(siteKey, {action}).then(resolve).catch(reject);
        });
      });

      setError('');
      return token;
    } catch (error) {
      setError('Unable to verify reCAPTCHA. Please try again.');
      return null;
    }
  }, []);

  return {
    error,
    execute,
    setError,
    siteKey
  };
};

const CaptchaChallenge = ({captcha}) => (
  <FormGroup>
    {!captcha.siteKey && (
      <Alert color="danger" className="mb-0">
        reCAPTCHA is not configured. Please set REACT_APP_RECAPTCHA_SITE_KEY.
      </Alert>
    )}
    {captcha.error && <Alert color="danger" className="mb-0">{captcha.error}</Alert>}
    {captcha.siteKey && !captcha.error && (
      <small className="text-muted">This form is protected by Google reCAPTCHA.</small>
    )}
  </FormGroup>
);

export default CaptchaChallenge;
